/**
 * ChatInput -- Monarch Chat Input
 *
 * STYLE: Warm dark palette with gold accents. Rounded corners.
 *
 * Features:
 *   - Clean textarea with solid border
 *   - Send button: solid bg-gold text-white
 *   - Auto-expanding with smooth transitions
 *   - Enter to send (Shift+Enter for newline)
 *   - Loading state disables input
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatInputProps {
  onSend: (message: string) => void;
  isThinking?: boolean;
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Icons
// ---------------------------------------------------------------------------

function SendIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
      <path
        d="M2.5 8h11M9 3.5L13.5 8 9 12.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none">
      <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MAX_CHARS = 2000;
const MAX_ROWS = 6;

export default function ChatInput({
  onSend,
  isThinking = false,
  placeholder = 'Ask about traffic, bookings, guest engagement...',
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Auto-resize the textarea based on content.
   */
  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const lineHeight = 22;
    const maxHeight = lineHeight * MAX_ROWS;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
  }, []);

  useEffect(() => {
    autoResize();
  }, [value, autoResize]);

  /**
   * Handle form submission.
   */
  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isThinking) return;

    onSend(trimmed);
    setValue('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [value, isThinking, onSend]);

  /**
   * Handle keyboard events.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter sends (Shift+Enter for newline)
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
        return;
      }
    },
    [handleSubmit],
  );

  const charCount = value.length;
  const isOverLimit = charCount > MAX_CHARS;
  const canSend = value.trim().length > 0 && !isOverLimit && !isThinking;

  return (
    <div className="border-t border-border bg-bg-card px-3 sm:px-6 py-3 sm:py-4">
      <div className="flex items-end gap-2 sm:gap-3">
        {/* Textarea */}
        <div className="relative flex-1">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            disabled={isThinking}
            className={`
              w-full resize-none
              bg-[var(--color-input-bg)] rounded-xl
              px-3 py-3 sm:px-4
              text-base sm:text-sm text-text-2 placeholder:text-text-3
              border border-border
              focus:outline-none focus:border-monarch-gold
              caret-monarch-gold
              transition-colors duration-150
              disabled:opacity-50 disabled:cursor-not-allowed
              ${isOverLimit ? 'border-red-500' : ''}
            `}
            style={{ lineHeight: '22px', maxHeight: `${22 * MAX_ROWS}px` }}
          />

          {/* Character counter */}
          {charCount > MAX_CHARS * 0.8 && (
            <span
              className={`
                absolute bottom-2 right-4 text-[10px] font-medium
                ${isOverLimit ? 'text-red-500' : 'text-text-3'}
              `}
            >
              {charCount}/{MAX_CHARS}
            </span>
          )}
        </div>

        {/* Send / Stop button -- larger touch target on mobile */}
        <button
          onClick={isThinking ? undefined : handleSubmit}
          disabled={!canSend && !isThinking}
          className={`
            flex-shrink-0 p-3.5 sm:p-3 rounded-xl border border-transparent
            transition-colors duration-150
            ${isThinking
              ? 'bg-red-600 text-white hover:bg-red-600/80'
              : canSend
                ? 'bg-monarch-gold text-white hover:bg-monarch-gold/90'
                : 'bg-bg-2 text-text-3 cursor-not-allowed border-border'
            }
          `}
          title={isThinking ? 'Stop generation' : 'Send message'}
          aria-label={isThinking ? 'Stop generation' : 'Send message'}
        >
          {isThinking ? <StopIcon /> : <SendIcon />}
        </button>
      </div>

      {/* Hint -- hidden on mobile */}
      <div className="hidden sm:flex items-center gap-3 mt-2 px-1">
        <span className="text-[10px] text-text-3 flex items-center gap-1.5 font-sans">
          Press
          <kbd className="px-1.5 py-0.5 bg-bg-2 rounded-xl text-[10px] font-mono border border-border text-text-3">
            Enter
          </kbd>
          to send,
          <kbd className="px-1.5 py-0.5 bg-bg-2 rounded-xl text-[10px] font-mono border border-border text-text-3">
            Shift+Enter
          </kbd>
          for newline
        </span>
      </div>
    </div>
  );
}
